import { logger } from 'monitor';
import { useEffect, useState } from 'react';

import { AspectRatioType, MIN_RESOLUTION } from '../../config';

const getHighestResolutionFromCamera = async (): Promise<{ width: number; height: number } | null> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: 'environment',
      },
    });

    const videoTrack = stream.getVideoTracks()[0];

    if ('getCapabilities' in videoTrack) {
      const capabilities = videoTrack.getCapabilities();
      const maxWidth = capabilities.width?.max;
      const maxHeight = capabilities.height?.max;

      stream.getTracks().forEach((track) => track.stop());

      if (maxWidth && maxHeight) {
        return { height: maxHeight, width: maxWidth };
      }
    }
    stream.getTracks().forEach((track) => track.stop());
    return null;
  } catch (error) {
    logger.error('Error getting highest resolution from camera: ', {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
};

const getMaxResolutionWithAspectRatio = (width: number, height: number, aspectRatio: number) => {
  let maxW = width;
  let maxH = Math.floor(width / aspectRatio);

  if (maxH > height) {
    maxH = height;
    maxW = Math.floor(height * aspectRatio);
  }

  return [maxW, maxH];
};

const getCameraStream = async ({ aspectRatio }: { aspectRatio: AspectRatioType }) => {
  const highestResolution = await getHighestResolutionFromCamera();
  let idealHeight, idealWidth;

  if (highestResolution) {
    const [maxW, maxH] = getMaxResolutionWithAspectRatio(
      highestResolution.width,
      highestResolution.height,
      aspectRatio
    );
    idealHeight = maxH;
    idealWidth = maxW;
  } else {
    idealHeight = MIN_RESOLUTION[aspectRatio].minHeight;
    idealWidth = MIN_RESOLUTION[aspectRatio].minWidth;
  }

  if (idealHeight > idealWidth) {
    // this is very weird, in portrait mode, height should be bigger than width
    // but I have to swap them to make it work
    [idealHeight, idealWidth] = [idealWidth, idealHeight];
  }

  return await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: 'environment',
      height: {
        ideal: idealHeight,
        min: idealHeight,
      },
      width: { ideal: idealWidth, min: idealWidth },
    },
  });
};

export const useCameraStream = ({ aspectRatio }: { aspectRatio: AspectRatioType }) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Warning! This will not work in strict mode!
  const releaseStream = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initStream = async () => {
      try {
        const fetchedStream = await getCameraStream({ aspectRatio });

        if (isMounted) {
          setStream(fetchedStream);
        }
      } catch (error) {
        logger.error('Failed to get camera stream', error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    initStream();

    return () => {
      isMounted = false;
      releaseStream();
    };
    // Run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspectRatio]);

  return {
    isLoading,
    releaseStream,
    stream,
  };
};
